import React from 'react';
import { createPortal } from 'react-dom';

import { Loader } from '../../inputs/Loader';
import { useIsNavigating } from '../../../hooks/useIsNavigating';
import { NavigationVeil } from './styles';

/**
 * Envuelve el `<Outlet/>` de un layout: mientras hay una navegación en curso
 * que merece feedback, superpone el `<Loader/>` sobre la pantalla.
 *
 * **No desmonta el contenido.** Ver el comentario de `NavigationVeil` para el
 * porqué — resumido: sustituirlo desplomaba la altura de la página y el
 * navegador subía el scroll al principio, y dentro del asistente eso hacía
 * perder de vista la barra de pestañas.
 *
 * OJO: no confundir con `components/navigation/Fallback`, que es otra cosa —
 * aquel cubre la suspensión de datos diferidos (`defer`/`Await`) DENTRO de una
 * ruta ya montada.
 *
 * Spec: docs/superpowers/specs/2026-08-14-feedback-navegacion-asistentes-design.md
 */
export const NavigationFallback = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const isNavigating = useIsNavigating();

  return (
    <>
      {children}
      {isNavigating &&
        createPortal(
          <NavigationVeil data-testid="navigation-veil" aria-busy="true">
            <Loader />
          </NavigationVeil>,
          document.body
        )}
    </>
  );
};
